import { createElement, useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import axios from '../../../lib/axios'
import { normalizeMetricStatus } from '../../../lib/metricStatus'
import {
  ChartData,
  CompetitorAnalysisData,
  ConfiguredCPI,
  CPIFrequency,
  CPIIntegrationItem,
  CPILibraryIntegration,
  CPILibraryItem,
  CPIManifest,
  CPIManifestUIDateRangeValue,
  CPIManifestUIFrequencyValue,
  CPISeverity,
  CPITimePeriod,
  CustomTrendsDataPayload,
  DataStatus,
  DetailedComparisonData,
  IFramework,
  IntegrationInstance,
  PredictionChartData,
  PredictionData,
  PredictionDataExtended,
  TrendsDataPayload
} from '../../../models'
import { formatCPIDisplayNameForURL, isCustomFramework } from '../../../lib/utils'
import { Checked, CustomColors } from './CPIDetails'
import {
  filterPredictionData,
  formatCustomTimePickerValue,
  getConfiguredInstancesApplicableToCpi,
  getEnabledInstances,
  getNotConfiguredInstancesApplicableToCpi,
  getPayloadTimePeriod,
  getPayloadTrendsChart,
  extractCpiFromPathname,
  isUUID,
  resolveSectionName
} from '../library/utils'
import {
  adjustDefaultTimePeriod,
  adjustTimePeriodValues,
  defaultFrequencyValues,
  defaultTimePeriodValues,
  getInsightSeverity
} from '../library/library.constants'
import dayjs from 'dayjs'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useAppSelector } from '../../../store/hooks'
import { accountSettingsSelector } from '../../../store/user/userSlice'
import { BenchmarkServiceV4, MetricsServiceV4 } from 'services'
import { isStaleAccountSessionError } from 'lib/accountSessionState'
import { HotToast } from 'ui/components/toast'
import { filterToCompatibleSources } from './compatibleSources'

export enum DevicesTabs {
  Trends = 'trends',
  OperationalView = 'operational_view',
  Benchmarking = 'benchmarking',
  About = 'about'
}

interface Props {
  cpiName: string | undefined
}

const useLibraryDetailsData = ({ cpiName }: Props) => {
  const location = useLocation()
  const navigate = useNavigate()
  const params = useParams()
  const [userConfiguredIntegrations, setUserConfiguredIntegrations] = useState<IntegrationInstance[]>([])
  const [cpi, setCpi] = useState<CPILibraryItem | null>(null)
  const [allCpis, setAllCpis] = useState<CPILibraryItem[]>([])
  const [cpiConfiguration, setCpiConfiguration] = useState<ConfiguredCPI | null>(null)
  const [manifestData, setManifestData] = useState<CPIManifest | null>(null)
  const [competitorAnalysisData, setCompetitorAnalysisData] = useState<CompetitorAnalysisData | null>(null)
  const [detailedComparisonData, setDetailedComparisonData] = useState<DetailedComparisonData | null>(null)
  const [renderingChart, setRenderingChart] = useState(true)
  const [tabValue, setTabValue] = useState<DevicesTabs>(location.pathname.split('/')?.pop() as DevicesTabs)
  const [key, setKey] = useState(0)
  const [trendsRefetchTrigger, setTrendsRefetchTrigger] = useState(0)

  const [active, setActive] = useState(false)
  const [showBenchmarkingTab, setShowBenchmarkingTab] = useState(false)
  const [initialLoad, setInitialLoad] = useState(false)
  const [activeSources, setActiveSources] = useState<IntegrationInstance[]>([])
  const [potentialSources, setPotentialSources] = useState<IntegrationInstance[]>([])
  const [checked, setChecked] = useState<Checked>({})
  const [favorite, setFavorite] = useState(Boolean(cpi?.favorite))
  const [hasPredictionsEnabled, setHasPredictionsEnabled] = useState(false)
  const isCategoryPage = Boolean(!cpiName?.includes('cpi'))

  const [stripes, setStripes] = useState<number | null>(null)
  const [selectedSeverity, setSelectedSeverity] = useState<CPISeverity>(CPISeverity.Critical)
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<CPITimePeriod>(CPITimePeriod.Month)
  const [customTimePeriodValue, setCustomTimePeriodValue] = useState<string>('')
  const [timePeriodValues, setTimePeriodValues] = useState<Array<CPITimePeriod | string>>(defaultTimePeriodValues)
  const [frequencyValues, setFrequencyValues] = useState<Array<CPIFrequency>>(defaultFrequencyValues)
  const [selectedFrequency, setSelectedFrequency] = useState<CPIFrequency>(CPIFrequency.Daily)
  const [selectedType, setSelectedType] = useState<string>('')
  const [predictionData, setPredictionData] = useState<PredictionChartData | null>(null)
  const [predictionFetched, setPredictionFetched] = useState(false)
  const previousCpi = useRef(cpiName)
  const currentCpi = useRef(cpiName)
  const fetchInProgressRef = useRef(false)
  const insightIntegrationIdsRef = useRef<string[] | null>(null)
  /* The platform's compatible-instances answer for the current CPI; null while none applies. */
  const compatibleInstanceNamesRef = useRef<string[] | null>(null)
  const [isSnowflake, setIsSnowflake] = useState(false)
  const [deactivateCpiV1, setDeactivateCpiV1] = useState(false)

  const [configuredInstancesApplicableToCpi, setConfiguredInstancesApplicableToCpi] = useState<IntegrationInstance[]>(
    []
  )
  const [notConfiguredInstancesApplicableToCpi, setNotConfiguredInstancesApplicableToCpi] = useState<
    IntegrationInstance[]
  >([])
  const [cpiSupportedAndUserConfiguredList, setCpiSupportedAndUserConfiguredList] = useState<IntegrationInstance[]>([])
  const [tempCpiSupportedAndUserConfiguredList, setTempCpiSupportedAndUserConfiguredList] = useState<
    IntegrationInstance[]
  >([])
  const [dataSourceValue, setDataSourceValue] = useState('All')
  const [dataSourceOptions, setDataSourceOptions] = useState<IntegrationInstance[]>([])
  const [manifestV2, setManifestV2] = useState(false)
  const [selectedDataSourceIds, setSelectedDataSourceIds] = useState<string[]>([''])
  const [fetchedDataSourcesIds, setFetchedDataSourcesIds] = useState(false)
  const [predictionModelAvailable, setPredictionModelAvailable] = useState<boolean>(false)
  const [daysUntilDataAvailable, setDaysUntilDataAvailable] = useState<number>(0)
  const [customColors, setCustomColors] = useState<CustomColors | null>(null)
  const [currentUrlCpi, setCurrentUrlCpi] = useState<string>('')

  const [customFramework, setCustomFramework] = useState<IFramework | null>(null)
  const accountSettings = useAppSelector(accountSettingsSelector)
  const framework = accountSettings?.cyFramework
  const fw = framework === 'onyxia' ? 'onyxia' : framework === 'nist' ? 'nist' : 'custom'

  useEffect(() => {
    const newCpiFromUrl = extractCpiFromPathname(location.pathname)

    if (currentUrlCpi !== '' && currentUrlCpi !== newCpiFromUrl) {
      setDataSourceOptions([])
      setCustomTimePeriodValue('')
    }

    setCurrentUrlCpi(newCpiFromUrl)
  }, [location.pathname, currentUrlCpi])

  useEffect(() => {
    const newCpiFromUrl = extractCpiFromPathname(location.pathname)

    if (currentUrlCpi !== '' && currentUrlCpi !== newCpiFromUrl) {
      setDataSourceOptions([])
    }
  }, [location.search, params.results])

  useEffect(() => {
    if (tempCpiSupportedAndUserConfiguredList.length > 0 && dataSourceOptions.length > 0) {
      const response: any[] = []

      tempCpiSupportedAndUserConfiguredList.forEach((n) => {
        const foundDataSource = dataSourceOptions.find((c) => c.id === n.id)
        if (foundDataSource) {
          response.push({
            ...n,
            syncStatus: foundDataSource.syncStatus
          })
        } else {
          response.push(n)
        }
      })

      setCpiSupportedAndUserConfiguredList(response)
    }
  }, [tempCpiSupportedAndUserConfiguredList, dataSourceOptions])

  useEffect(() => {
    getNameForManifest(allCpis, cpiName)
  }, [allCpis])

  useEffect(() => {
    if (!manifestData || !initialLoad || !cpi) return

    const pathParts = location.pathname.split('/')
    const currentTab = pathParts[pathParts.length - 1] as DevicesTabs
    const currentCpiName = formatCPIDisplayNameForURL(cpi.display_name || cpiName || '')

    // Determine target tab based on CPI active status
    const targetTab = active ? DevicesTabs.Trends : DevicesTabs.About
    const targetPath = `/library/cpis/${currentCpiName}/${targetTab}`

    // Only redirect if we're not already on the correct tab
    if (currentTab !== targetTab) {
      setTabValue(targetTab)
      navigate(targetPath, { replace: true })
    }
  }, [cpi?.name, active, initialLoad, manifestData])

  const getNameForManifest = (allCpis: CPILibraryItem[], name = '') => {
    if (!allCpis.length) {
      return {
        cpiManifestName: '',
        currentCpiName: '',
        cpiSupportedAndUserConfiguredList: []
      }
    }
    const finalName = name || cpiName
    const currentCpi = allCpis.find((n: CPILibraryItem) => formatCPIDisplayNameForURL(n.display_name) === finalName)
    let cpiNameForManifest = finalName
    let fetchedCpi: CPILibraryItem | null = null
    if (currentCpi) {
      fetchedCpi = currentCpi
      cpiNameForManifest = currentCpi.name
    } else {
      const newCpi = allCpis.find((n: CPILibraryItem) => n.name === finalName)
      if (newCpi) {
        fetchedCpi = newCpi
        cpiNameForManifest = newCpi?.name ?? ''
      }
    }
    setCpi(fetchedCpi)

    if (fetchedCpi) {
      setFavorite(fetchedCpi.favorite)
    }

    return {
      cpiManifestName: cpiNameForManifest || '',
      currentCpiName: fetchedCpi?.name || ''
    }
  }

  const getActiveSources = useCallback(
    async (dataSourcesConfiguredForCpi: CPIIntegrationItem[]): Promise<IntegrationInstance[] | null> => {
      if (!dataSourcesConfiguredForCpi) return null
      // eslint-disable-next-line array-callback-return
      let activeSourcesIds: string[] = dataSourcesConfiguredForCpi.map((n: any) => {
        if (!n.deleted) {
          return n.id
        }
      })
      activeSourcesIds = activeSourcesIds.filter(Boolean)
      const activeSourcesPromises = activeSourcesIds.map(async (c: string) => {
        const resp = await axios.get(`/api/v3/integrations/config/${c}`)

        return resp.data.data
      })
      const activeSourcesResult = await Promise.all(activeSourcesPromises)
      setActiveSources(activeSourcesResult)

      return activeSourcesResult
    },
    []
  )

  const deactivateCpi = useCallback(() => {
    setActive(false)
  }, [])

  const refetchCpi = useCallback(async () => {
    if (!cpi) return

    try {
      const res = await axios.get<{ data: { cpis: CPILibraryItem[] } }>(`/api/v3/cpis`)

      if (res.status === 200) {
        const {
          data: {
            data: { cpis: allCpis }
          }
        } = res

        const newCpi = allCpis.find((n: CPILibraryItem) => n.name === cpi.name)
        if (newCpi) {
          setCpi(newCpi)
        }
      }
    } catch (e) {
      console.error(e)
    }
  }, [cpi])

  const updateFavorite = useCallback(async () => {
    if (!cpi) return

    try {
      if (favorite) {
        setFavorite(false)

        await axios.delete('/api/v3/cpis/favorites', {
          data: {
            name: cpi.name
          }
        })
      } else {
        setFavorite(true)

        await axios.post('/api/v3/cpis/favorites', {
          name: cpi.name
        })
      }

      await refetchCpi()
    } catch (e) {
      console.error(e)
      setFavorite(favorite)
    }
  }, [cpi, favorite])

  const fetchUserConfiguredIntegrations = useCallback(async () => {
    try {
      const res = await axios.get<{ instances: IntegrationInstance[] }>(
        '/api/v3/integrations/configured?loadStatus=true'
      )

      if (res.status === 200) {
        const { instances } = res.data
        setUserConfiguredIntegrations(instances)

        return instances as IntegrationInstance[]
      }
    } catch (e) {
      console.error(e)
    }

    return []
  }, [])

  const fetchAllCpis = useCallback(async (): Promise<CPILibraryItem[]> => {
    try {
      const res = await axios.get(`/api/v3/cpis`)
      if (res.status === 200) {
        const {
          data: {
            data: { cpis }
          }
        } = res
        setAllCpis(cpis)

        return cpis
      }
    } catch (e) {
      console.error(e)
    }

    return []
  }, [])

  /* Which configured instances the platform will let feed this metric. Legacy CPIs get null — no
   * answer applies and the client-side join stands. A failed lookup answers empty, so the candidate
   * lists fail closed rather than falling back to the join the platform would refuse. */
  const fetchCompatibleInstanceNames = useCallback(async (metricSlug: string, isV2: boolean) => {
    if (!isV2) return null

    try {
      const products = await MetricsServiceV4.getCompatibleProducts(metricSlug)

      return products.map((product) => product.instance_name)
    } catch (e) {
      console.error(e)
      if (!isStaleAccountSessionError(e)) {
        toast.error(() =>
          createElement(HotToast, { text: 'Error', description: 'Failed loading compatible data sources.' })
        )
      }

      return []
    }
  }, [])

  const fetchManifest = useCallback(
    async (cpiManifestName: string, configuredIntegrations: IntegrationInstance[]) => {
      try {
        const res = await axios.get<{ data: CPIManifest | null }>(`/api/v3/cpis/${cpiManifestName}/details`)

        if (res.status === 200) {
          const { data } = res.data
          setManifestData(data)
          setManifestV2(Boolean(data?.is_v2))

          if (!data) return null

          compatibleInstanceNamesRef.current = await fetchCompatibleInstanceNames(cpiManifestName, Boolean(data.is_v2))

          const potentialSources = filterToCompatibleSources(
            configuredIntegrations.filter((c) =>
              data.supported_integrations.some((n) => n.name === c.integration_name)
            ),
            compatibleInstanceNamesRef.current
          )
          setPotentialSources(potentialSources)

          if (isCustomFramework(framework)) {
            try {
              const customFramework = await axios.get('/api/v3/frameworks/custom')
              if (customFramework && data) {
                setCustomFramework(customFramework.data.framework)
                const categories = customFramework.data.framework.data.functions
                const thisCategory = data.category.title || ''
                const foundCategory = categories.find((n: any) => n.title === thisCategory)
                if (foundCategory) {
                  setCustomColors({
                    chip_bg_color: foundCategory.chip_bg_color,
                    chip_color: foundCategory.chip_color
                  })
                }
              }
            } catch (err) {
              console.error(err)
            }
          }

          let defaultSeverity = data.ui.trend_chart.user_input.visualization_param.default_section as any
          if (data.ui.trend_chart.user_input.visualization_param.sections_name === 'email_sizes') {
            const defaultSectionName = data.ui.trend_chart.user_input.visualization_param.default_section
            const findItem = data.ui.trend_chart.user_input.visualization_param.sections.find(
              (item) => item.name === defaultSectionName
            )
            defaultSeverity = findItem?.display_name
          }
          const defSec = data.ui.trend_chart.user_input.visualization_param.sections.find(
            (c) => c.name === defaultSeverity
          )
          const defaultFrequencyValue = data.ui.trend_chart.frequency.default as CPIFrequency
          const defaultTimePeriod = data.ui.trend_chart.date_range.default
          const defaultTimePeriodValues: CPIManifestUIDateRangeValue[] = data.ui.trend_chart.date_range.values
          const adjustedTimePeriodValues = adjustTimePeriodValues(defaultTimePeriodValues)
          const adjustedDefaultTimePeriod = adjustDefaultTimePeriod(defaultTimePeriod, defaultFrequencyValue)
          const defaultType = data.ui.trend_chart.type ? data.ui.trend_chart.type.values[0].display_name : null

          const insightFilters = localStorage.getItem('insightFilters')

          if (insightFilters) {
            const parsed = JSON.parse(insightFilters)

            // Store integration IDs in ref for fetchCpiConfig to use
            if (parsed.integrationIds && parsed.integrationIds.length > 0) {
              insightIntegrationIdsRef.current = parsed.integrationIds
            }

            // Applied independently of the time range below: an insight can carry a frequency and
            // severity without a resolvable period, and those must still survive the redirect.
            if (parsed.frequency) {
              setSelectedFrequency(parsed.frequency as CPIFrequency)
            }
            setSelectedSeverity(getInsightSeverity(parsed.severity, data))

            const { dateFrom, dateTo } = parsed.dates || {}
            if (dateFrom && dateTo) {
              setCustomTimePeriodValue(formatCustomTimePickerValue(dateFrom, dateTo))
              setSelectedTimePeriod(CPITimePeriod.Custom)
            } else if (parsed.timePeriod) {
              setSelectedTimePeriod(parsed.timePeriod as CPITimePeriod)
            } else {
              setSelectedTimePeriod(adjustedDefaultTimePeriod)
            }

            setTimeout(() => {
              localStorage.removeItem('insightFilters')
            }, 1000)
          } else if (location.state) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const { dates, frequency, severity, timePeriod } = location.state
            setSelectedSeverity(severity)
            setSelectedFrequency(frequency)
            if (dates) {
              const formattedValue = formatCustomTimePickerValue(dates.dateFrom, dates.dateTo)
              setCustomTimePeriodValue(formattedValue)
              setSelectedTimePeriod(CPITimePeriod.Custom)
            } else if (timePeriod) {
              setSelectedTimePeriod(timePeriod)
            }
          } else {
            setSelectedSeverity(defSec ? defSec.display_name : defaultSeverity)
            setSelectedFrequency(defaultFrequencyValue)
            setSelectedTimePeriod(adjustedDefaultTimePeriod)
            if (defaultType) {
              setSelectedType(defaultType)
            }
          }

          setFrequencyValues(
            data.ui.trend_chart.frequency.values.map((n: CPIManifestUIFrequencyValue) => n.name) as CPIFrequency[]
          )
          setTimePeriodValues(adjustedTimePeriodValues)

          return data
        }
      } catch (e) {
        console.error(e)
        const message = (e as any).response?.data?.code || ''
        if (message === 'manifest_not_found') {
          return null
        }
      }

      return null
    },
    [initialLoad, renderingChart, framework, fetchCompatibleInstanceNames]
  )

  const fetchCompetitorAnalysis = useCallback(async (cpiManifestName: string) => {
    try {
      const competitorAnalysisData = await BenchmarkServiceV4.getPerformanceMetrics(cpiManifestName)
      setCompetitorAnalysisData(competitorAnalysisData)

      return competitorAnalysisData
    } catch (e) {
      console.error(e)
    }

    return null
  }, [])

  const fetchDetailedComparison = useCallback(async (cpiManifestName: string) => {
    try {
      const detailedComparisonData = await BenchmarkServiceV4.getScore(cpiManifestName)
      setDetailedComparisonData(detailedComparisonData)

      return detailedComparisonData
    } catch (e) {
      console.error(e)
    }

    return null
  }, [])

  const fetchBenchmarkingData = useCallback(
    async (cpiManifestName: string) => {
      const promises = [fetchCompetitorAnalysis(cpiManifestName), fetchDetailedComparison(cpiManifestName)]

      try {
        const res = await Promise.all(promises)
        const benchmarkingData = res.filter((c) => !!c)
        setShowBenchmarkingTab(benchmarkingData.length > 0)
      } catch (err) {
        console.error(err)
      }
    },
    [fetchCompetitorAnalysis, fetchDetailedComparison]
  )

  const fetchCpiConfig = useCallback(
    async (
      currentCpiName: string,
      manifestData: CPIManifest | null,
      userConfiguredIntegrations: IntegrationInstance[]
    ) => {
      try {
        const res = await axios.get<{ data: ConfiguredCPI }>(`/api/v3/cpis/${currentCpiName}?cpi-config`)

        if (res.status === 200) {
          const {
            data: { data: cpiConfiguration }
          } = res
          setActive(true)
          setCpiConfiguration(cpiConfiguration)
          let cpiSupportedAndUserConfiguredList: IntegrationInstance[] = []

          if (manifestData) {
            const cpiSupportedIntegrationNames = manifestData.supported_integrations.map(
              (n: CPILibraryIntegration) => n.name
            )
            const linkedInstanceIds = (cpiConfiguration.integrations || []).map((n) => n.id)
            const newList = filterToCompatibleSources(
              userConfiguredIntegrations.filter((n) => cpiSupportedIntegrationNames.includes(n.integration_name)),
              compatibleInstanceNamesRef.current,
              linkedInstanceIds
            )
            cpiSupportedAndUserConfiguredList = newList
            setCpiSupportedAndUserConfiguredList(newList)
          }

          const configuredIntegrations = cpiConfiguration.integrations as CPIIntegrationItem[]
          if (!configuredIntegrations) return

          const thisActiveDataSources = await getActiveSources(configuredIntegrations)
          const snowflake = cpiConfiguration?.is_v2
          setIsSnowflake(Boolean(cpiConfiguration?.is_v2))

          /*
           * CPI was active in v1, then in backoffice was migrated to v2, so now we have to flag for deactivation in v1
           * before adding data sources for v2
           * */
          if (snowflake && Array.isArray(thisActiveDataSources) && thisActiveDataSources.every((c) => !c.is_v2)) {
            setDeactivateCpiV1(true)
          }
          const enabledInstances = getEnabledInstances(
            configuredIntegrations,
            userConfiguredIntegrations,
            cpiConfiguration.is_v2
          )
          setChecked(enabledInstances)
          const deletedInstances = configuredIntegrations.find((item) => item.deleted)
          let unique = [] as IntegrationInstance[]
          if (deletedInstances) {
            const list: IntegrationInstance[] = []
            configuredIntegrations.forEach((item) => {
              if (item.deleted) {
                const obj = {
                  connection_id: '',
                  created_at: '',
                  created_by: '',
                  description: '',
                  id: item.id,
                  config_name: item.configName,
                  integration_name: item.name,
                  name: item.configName,
                  prefect_deployment_id: '',
                  source_id: '',
                  tenant_id: '',
                  updated_at: '',
                  updated_by: '',
                  deleted: true
                } as IntegrationInstance
                list.push(obj)

                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                setChecked((prevState) => ({ ...prevState, [item.configName]: true }))
              } else {
                const findItem = cpiSupportedAndUserConfiguredList.find((cpi) => cpi.name === item.name)
                if (findItem) list.push(findItem)
              }
            })
            const combineItems = [...cpiSupportedAndUserConfiguredList, ...list]
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            unique = [...new Map(combineItems.map((item) => [item.name, item])).values()]
            setTempCpiSupportedAndUserConfiguredList(unique)
          } else setTempCpiSupportedAndUserConfiguredList(cpiSupportedAndUserConfiguredList)

          const configuredInstancesIds: string[] = cpiConfiguration.integrations?.map((n) => n.id) || []
          const filterList = unique.length ? unique : cpiSupportedAndUserConfiguredList
          const allConfiguredInstances = filterList.filter((n) => configuredInstancesIds.includes(n.id))

          if (cpiConfiguration.is_v2) {
            allConfiguredInstances.filter((c) => c.is_v2)
          } else {
            allConfiguredInstances.filter((c) => !c.is_v2)
          }
          setDataSourceOptions(allConfiguredInstances)

          const configuredInstancesApplicableToCpi = getConfiguredInstancesApplicableToCpi(
            configuredIntegrations,
            userConfiguredIntegrations
          )
          setConfiguredInstancesApplicableToCpi(configuredInstancesApplicableToCpi)

          // Check if insight filters specify data sources (stored in ref by fetchManifest)
          const insightIds = insightIntegrationIdsRef.current
          if (insightIds && insightIds.length > 0) {
            // Match insight integration IDs against configured data sources
            const matchedInstances = allConfiguredInstances.filter(
              (opt) => insightIds.includes(opt.id) || insightIds.includes(opt.instance_name || '')
            )

            if (matchedInstances.length > 0) {
              const matchedIds = cpiConfiguration.is_v2
                ? matchedInstances.map((c) => c.instance_name || '')
                : matchedInstances.map((c) => c.id)
              setSelectedDataSourceIds(matchedIds)
              setDataSourceValue(
                matchedInstances.length === 1
                  ? matchedInstances[0].name
                  : `${matchedInstances.length} of ${allConfiguredInstances.length}`
              )
            } else {
              setDataSourceValue(`All (${cpiConfiguration.integrations.length})`)
              setSelectedDataSourceIds(configuredInstancesIds)
            }
            insightIntegrationIdsRef.current = null
          } else {
            setDataSourceValue(`All (${cpiConfiguration.integrations.length})`)
            setSelectedDataSourceIds(configuredInstancesIds)
          }
          setFetchedDataSourcesIds(true)

          if (!manifestData) return
          const potentialSources = filterToCompatibleSources(
            getNotConfiguredInstancesApplicableToCpi(manifestData, configuredIntegrations, userConfiguredIntegrations),
            compatibleInstanceNamesRef.current
          )
          setNotConfiguredInstancesApplicableToCpi(potentialSources)
        }
      } catch (e) {
        console.error(e)
        setActive(false)
        setTabValue(DevicesTabs.About)
        const trendsPath = location.pathname.split('/')
        trendsPath.pop()
        const aboutPath = `${trendsPath.join('/')}/about`
        navigate(`${aboutPath}`, { replace: true })
        setActiveSources([])

        if (!manifestData) return
        const cpiSupportedIntegrationsNames = manifestData.supported_integrations.map((n) => n.name)
        const notConfiguredInstancesApplicableToCpi = filterToCompatibleSources(
          userConfiguredIntegrations.filter((n) => cpiSupportedIntegrationsNames.includes(n.integration_name)),
          compatibleInstanceNamesRef.current
        )
        setNotConfiguredInstancesApplicableToCpi(notConfiguredInstancesApplicableToCpi)
      }
    },
    [userConfiguredIntegrations]
  )

  // TODO: ONYXIA:7328 Temporarily disabled predictions feature - re-enable when ready
  const checkAIPredictions = () => {
    // _manifestData: CPIManifest | null
    setHasPredictionsEnabled(false)
    return false
    // Original logic:
    // if (manifestData && manifestData.predictions && manifestData.predictions.is_supported) {
    //   setHasPredictionsEnabled(true)
    //   return true
    // } else {
    //   setHasPredictionsEnabled(false)
    //   return false
    // }
  }

  const fetchPredictionData = async (
    cpiName: string,
    severity = selectedSeverity,
    dataSourceName = ''
  ): Promise<PredictionData[]> => {
    const { cpiManifestName } = getNameForManifest(allCpis, cpiName)
    const thisSelectedSource = activeSources.find((n) => n.name === dataSourceValue)

    const getSourceName = () => {
      if (!dataSourceName) {
        return thisSelectedSource
          ? thisSelectedSource.integration_name
          : dataSourceValue.toLowerCase().includes('all (')
          ? 'all'
          : activeSources.length > 0
          ? activeSources[0].integration_name
          : ''
      } else {
        if (dataSourceName === 'all') return 'all'
        const integrationName = activeSources.find((n) => n.id === dataSourceName)

        if (integrationName) {
          return integrationName.integration_name
        }
      }
    }
    let sourceName = getSourceName()
    const sourceId =
      dataSourceName ||
      (activeSources.length > 0 ? (thisSelectedSource ? thisSelectedSource.id : activeSources[0].id) : '')

    if (sourceId.length > 0 && sourceName !== 'all') {
      const sourceSplit = sourceId.split('-')
      sourceName = `${sourceName}_${sourceSplit[0]}`
    }
    const payload = {
      severity_level: severity.toLowerCase(),
      datasource_name: sourceName
    }

    try {
      const res = await axios.post(`/api/v3/ai/predictions/cpis/${cpiManifestName}?predictions-data`, payload)

      const getIntegrationId = () => {
        if (!dataSourceName) {
          return activeSources.length > 0
            ? thisSelectedSource
              ? thisSelectedSource.id
              : dataSourceValue.toLowerCase().includes('all (')
              ? 'all'
              : activeSources[0].id
            : ''
        }

        return dataSourceName
      }

      try {
        const firstPayload: any = {
          section: resolveSectionName(severity, manifestData)
        }
        if (getIntegrationId() !== 'all') {
          firstPayload.integration_id = getIntegrationId()
        }
        const firstDataRes = await axios.post(
          `/api/v3/cpis/${cpiManifestName}/data/first?days-until-available-predictions`,
          firstPayload
        )

        if (firstDataRes.status.toString().includes('20')) {
          const diff = dayjs().diff(firstDataRes.data.data.date || '', 'days')
          setDaysUntilDataAvailable(diff)
        }
      } catch (err) {
        console.error(err)
      }

      if (res.data.statusCode.toString().includes('20')) {
        setPredictionModelAvailable(true)
        return res.data.body.predictions.map((n: any) => ({ xValue: n.date, yValue: n.prediction }))
      }

      if (res.data.statusCode === 404 && res.data.body?.message === 'Model not available') {
        setPredictionModelAvailable(false)
        return []
      }
    } catch (e) {
      console.error(e)
    }

    return []
  }

  const fetchTrendsDataForPredictions = useCallback(
    async (payload: TrendsDataPayload, cpiName: string, predictionData: PredictionData[]) => {
      const { cpiManifestName } = getNameForManifest(allCpis, cpiName)

      const url = isSnowflake
        ? `/api/v4/metrics/${cpiManifestName}/timeseries?hook-timeseries`
        : `/api/v3/cpis/${cpiManifestName}/data?fetch-trends-chart-data`

      const instanceNames =
        payload.integration_ids && payload.integration_ids.length > 0
          ? payload.integration_ids
          : cpi
          ? cpi.configuration?.integrations.filter((c) => !c.deleted).map((c) => c.instanceName)
          : []
      const resolvedSection = resolveSectionName(payload.section, manifestData)
      const snowflakePayload = {
        metric_request: {
          section: resolvedSection,
          frequency: selectedFrequency,
          instance_names: instanceNames,
          time_period: payload && payload.timePeriod ? payload.timePeriod : getPayloadTimePeriod(selectedTimePeriod)
        }
      }
      const res = await axios.post(
        url,
        isSnowflake
          ? snowflakePayload
          : {
              ...payload,
              section: resolvedSection
            }
      )

      if (res && res.status.toString().includes('20')) {
        if (isSnowflake) {
          const { data }: { data: ChartData } = res
          data.status = normalizeMetricStatus(data.status)
          const today = new Date().toISOString()
          const todayDate = today.slice(0, 10)
          const dataLastPoint = data.values[data.values.length - 1].xValue.slice(0, 10)
          if (dataLastPoint === todayDate) {
            data.values.pop()
          }

          const allValues: PredictionDataExtended[] = [
            ...data.values.map((n) => ({ xValue: n.xValue, yValue: n.yValue, prediction: false })),
            ...predictionData.map((n) => ({ ...n, prediction: true }))
          ]

          const chartData: PredictionChartData = {
            ...data,
            values: allValues
          }
          const filteredData: PredictionDataExtended[] = filterPredictionData(2, chartData)
          setPredictionData({
            ...chartData,
            values: [...filteredData]
          })
          setPredictionFetched(true)

          return data
        } else {
          const { data }: { data: ChartData } = res.data
          data.status = normalizeMetricStatus(data.status)
          if (data.status === DataStatus.Initializing || data.status === DataStatus.Recalculating) {
            setPredictionData(null)
          } else {
            const allValues: PredictionDataExtended[] = [
              ...data.values.map((n) => ({ xValue: n.xValue, yValue: n.yValue, prediction: false })),
              ...predictionData.map((n) => ({ ...n, prediction: true }))
            ]

            const chartData: PredictionChartData = {
              ...data,
              values: allValues
            }
            const filteredData: PredictionDataExtended[] = filterPredictionData(2, chartData)
            setPredictionData({
              ...chartData,
              values: [...filteredData]
            })
          }

          setPredictionFetched(true)

          return data
        }
      }

      return null
    },
    [allCpis, userConfiguredIntegrations, cpiName, cpi, isSnowflake]
  )

  const initialFetchPredictionData = useCallback(async () => {
    const predictionValues: PredictionData[] = await fetchPredictionData(cpiName || '')

    let payload: CustomTrendsDataPayload = getPayloadTrendsChart({
      severity: selectedSeverity,
      selectedDataSourceIds,
      selectedTimePeriod: CPITimePeriod.ThreeMonths,
      selectedFrequency: CPIFrequency.Daily
    })
    if (customTimePeriodValue.length > 0) {
      payload = getPayloadTrendsChart({
        severity: selectedSeverity,
        selectedDataSourceIds,
        // selectedTimePeriod: CPITimePeriod.ThreeMonths,
        // selectedFrequency: CPIFrequency.Daily
        // Changing this to fit when redirecting from Insights page to search data with correct params
        customTimePeriodValue,
        selectedTimePeriod,
        selectedFrequency
      })
    }

    fetchTrendsDataForPredictions(
      {
        ...payload,
        section: resolveSectionName(payload.section, manifestData) as any
      },
      cpiName || '',
      predictionValues
    )
  }, [selectedSeverity, selectedDataSourceIds, customTimePeriodValue, selectedTimePeriod, selectedFrequency, cpiName])

  useEffect(() => {
    if (initialLoad && manifestData) {
      const hasPredictionEnabled = checkAIPredictions()

      if (hasPredictionEnabled && fetchedDataSourcesIds && selectedDataSourceIds) {
        initialFetchPredictionData().catch((e) => e)
      } else if (!fetchedDataSourcesIds) {
        //
      } else {
        setPredictionFetched(true)
      }
    }
  }, [
    initialLoad,
    cpiName,
    isSnowflake,
    selectedDataSourceIds,
    fetchedDataSourcesIds,
    allCpis,
    selectedSeverity,
    activeSources
  ])

  const refetchPredictionData = async ({
    thisCpiName,
    severity,
    selectedSources,
    timePeriod
  }: {
    thisCpiName: string
    severity?: CPISeverity
    selectedSources?: string[]
    timePeriod?: CPITimePeriod
  }) => {
    const sourcesForPrediction = selectedSources || selectedDataSourceIds

    const res = await fetchPredictionData(
      thisCpiName || cpiName || '',
      severity || selectedSeverity,
      sourcesForPrediction.length === 1 ? sourcesForPrediction[0] : 'all' // Use UUIDs
    )

    let integrationIds = sourcesForPrediction
    if (isSnowflake && integrationIds.length > 0 && isUUID(integrationIds[0])) {
      integrationIds = dataSourceOptions
        .filter((c) => sourcesForPrediction.includes(c.id || ''))
        .map((c) => c.instance_name || '')
    }

    const payload: any = getPayloadTrendsChart({
      severity: severity ? severity.toLowerCase() : (selectedSeverity.toLowerCase() as any),
      selectedDataSourceIds: integrationIds, // Use transformed IDs for timeseries
      selectedTimePeriod: timePeriod || CPITimePeriod.ThreeMonths,
      customTimePeriodValue,
      selectedFrequency: selectedFrequency
    })

    return await fetchTrendsDataForPredictions(
      {
        ...payload,
        section: resolveSectionName(severity || selectedSeverity, manifestData)
      },
      cpiName || '',
      res
    )
  }

  const fetchAll = useCallback(
    async (name = '') => {
      if ((name === previousCpi.current && name !== '') || fetchInProgressRef.current) return
      fetchInProgressRef.current = true
      previousCpi.current = name

      try {
        const configuredIntegrations = await fetchUserConfiguredIntegrations()

        const cpis = await fetchAllCpis()
        const { cpiManifestName, currentCpiName } = getNameForManifest(cpis, name)
        const manifestData = await fetchManifest(cpiManifestName, configuredIntegrations)

        if (!manifestData) return

        checkAIPredictions()

        await Promise.all([
          fetchCpiConfig(currentCpiName, manifestData, configuredIntegrations),
          fetchBenchmarkingData(cpiManifestName)
        ])
      } catch (e) {
        console.error(e)
      } finally {
        setInitialLoad(true)
        fetchInProgressRef.current = false
      }
    },
    [userConfiguredIntegrations, cpiName, activeSources]
  )

  useEffect(() => {
    if (!isCategoryPage && !initialLoad) {
      fetchAll()
        .catch((e) => e)
        .finally(() => {
          if (currentCpi) {
            currentCpi.current = cpiName
          }
        })
    }

    return () => {
      previousCpi.current = ''
    }
  }, [isCategoryPage, cpiName, initialLoad, location, key])

  useEffect(() => {
    if (initialLoad && currentCpi && currentCpi.current !== '' && currentCpi.current !== cpiName) {
      setInitialLoad(false)
    }
  }, [currentCpi, cpiName])

  const triggerTrendsRefetch = useCallback(() => {
    setTrendsRefetchTrigger((prev) => prev + 1)
  }, [])

  return {
    key,
    setKey,
    userConfiguredIntegrations,
    cpi,
    customColors,
    setInitialLoad,
    fetchPredictionData,
    allCpis,
    selectedType,
    setSelectedType,
    manifestV2,
    cpiConfiguration,
    manifestData,
    competitorAnalysisData,
    detailedComparisonData,
    active,
    showBenchmarkingTab,
    initialLoad,
    activeSources,
    checked,
    setChecked,
    configuredInstancesApplicableToCpi,
    notConfiguredInstancesApplicableToCpi,
    cpiSupportedAndUserConfiguredList,
    dataSourceValue,
    dataSourceOptions,
    deactivateCpi,
    favorite,
    updateFavorite,
    selectedSeverity,
    selectedTimePeriod,
    timePeriodValues,
    frequencyValues,
    selectedFrequency,
    setSelectedSeverity,
    setSelectedTimePeriod,
    setSelectedFrequency,
    setDataSourceValue,
    selectedDataSourceIds,
    setSelectedDataSourceIds,
    fetchCpiConfig,
    fetchAll,
    stripes,
    setStripes,
    customTimePeriodValue,
    setCustomTimePeriodValue,
    hasPredictionsEnabled,
    predictionData,
    setPredictionData,
    refetchPredictionData,
    predictionModelAvailable,
    daysUntilDataAvailable,
    customFramework,
    framework,
    fw,
    renderingChart,
    setRenderingChart,
    tabValue,
    setTabValue,
    predictionFetched,
    isSnowflake,
    deactivateCpiV1,
    setDeactivateCpiV1,
    potentialSources,
    triggerTrendsRefetch,
    trendsRefetchTrigger
  }
}

export default useLibraryDetailsData
